import { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cc from 'classcat';
import dayjs from 'dayjs';
import { EIconSize } from 'utils/constants/app/ui';
import { timeoutDuration } from 'utils/constants/dateInfo/time-numbers';
import { TYPE_DATE_PICKER } from 'utils/constants/sportsbook/results';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { dateFormat } from 'utils/date-manipulation/date-format';
import { isMobile } from 'utils/is-mobile';
import { CustomRangePicker } from 'components/CustomRangePicker';
import { GlobalIcon } from 'components/GlobalIcon';
import { setDate } from 'store/actions/results-data';
import { getResultsDate } from 'store/selectors/results-data';
import './index.less';

type Props = {
  disabled?: boolean;
};
const DAY_INCREMENT = 1;
const DAY_DECREMENT = -1;

export const DateFilter: FC<Props> = ({ disabled }): ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const resultsDate = useSelector(getResultsDate);
  const [isToday, setIsToday] = useState(false);
  const [arrowLeftAnimating, setArrowLeftAnimating] = useState(false);
  const [arrowRightAnimating, setArrowRightAnimating] = useState(false);

  const [startDate, setStartDate] = useState(dayjs(resultsDate.from));
  const [endDate, setEndDate] = useState(dayjs(resultsDate.to));

  useEffect(() => {
    !TYPE_DATE_PICKER &&
      setIsToday(resultsDate.to === dayjs().endOf('day').valueOf());
  }, [resultsDate]);

  useEffect(() => {
    TYPE_DATE_PICKER &&
      dispatch(
        setDate({
          from: dayjs(startDate).startOf('day').valueOf(),
          to: dayjs(endDate).endOf('day').valueOf()
        })
      );
  }, [startDate, endDate]);

  const changeDate = useCallback(
    (action: 'add' | 'subtract') => {
      if (TYPE_DATE_PICKER) {
        return;
      }

      const selectedDate = dayjs(resultsDate.to)
        .add(action === 'add' ? DAY_INCREMENT : DAY_DECREMENT, 'd')
        .valueOf();

      dispatch(
        setDate({
          from: selectedDate,
          to: selectedDate
        })
      );
    },
    [resultsDate]
  );

  const onArrowClick = useCallback(
    (dir: 'left' | 'right' = 'left') => {
      if (TYPE_DATE_PICKER) {
        return;
      }

      if (dir === 'left') {
        setArrowLeftAnimating(true);
        changeDate('subtract');
        const timeout = setTimeout(() => {
          setArrowLeftAnimating(false);

          clearTimeout(timeout);
        }, timeoutDuration.DURATION_200);
      } else {
        setArrowRightAnimating(true);
        changeDate('add');
        const timeout = setTimeout(() => {
          setArrowRightAnimating(false);

          clearTimeout(timeout);
        }, timeoutDuration.DURATION_200);
      }
    },
    [changeDate]
  );

  return (
    <div className="dateFilter">
      <div className="dateFilter__label">{t('sportsbook.date')}</div>
      <div
        className={cc([
          'dateFilter__picker',
          {
            'dateFilter__picker--disabled': disabled
          }
        ])}
      >
        {TYPE_DATE_PICKER ? (
          <CustomRangePicker
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            fromDate={startDate}
            toDate={endDate}
            pickerValue={dayjs(startDate)}
            period={{ value: 2, unit: 'day' }}
            size={isMobile() ? 'large' : 'small'}
            modeDatePicker={isMobile()}
            bordered
          />
        ) : (
          <>
            <GlobalIcon
              lib="generic"
              name="caretLeft"
              theme="default"
              size={EIconSize._10}
              className={cc([
                'dateFilter__pickerArrow',
                {
                  'dateFilter__pickerArrow--noHover': isMobile(),
                  'dateFilter__pickerArrow--animating':
                    arrowLeftAnimating && isMobile(),
                  'dateFilter__pickerArrow--disabled': disabled
                }
              ])}
              onClick={() => {
                if (!disabled) {
                  onArrowClick('left');
                }
              }}
            />
            <div
              className="dateFilter__pickerDate"
              title={dayjs(resultsDate.to).format(
                dateFormat({
                  date: SpringConfigs.DT.longDate,
                  includeWeekday: true,
                  dateTimeSeparator: ' | '
                })
              )}
            >
              {dayjs(resultsDate.to).format(
                dateFormat({
                  date: SpringConfigs.DT.longDate,
                  includeWeekday: true,
                  dateTimeSeparator: ' | '
                })
              )}
            </div>
            <GlobalIcon
              lib="generic"
              name="caretRight"
              theme="default"
              size={EIconSize._10}
              className={cc([
                'dateFilter__pickerArrow',
                {
                  'dateFilter__pickerArrow--noHover': isMobile(),
                  'dateFilter__pickerArrow--animating':
                    arrowRightAnimating && isMobile(),
                  'dateFilter__pickerArrow--disabled': isToday || disabled
                }
              ])}
              onClick={() => {
                if (!(isToday || disabled)) {
                  onArrowClick('right');
                }
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};
